// client/src/App.js
import './App.css';
import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { useWindowSize } from 'react-use';
import Confetti from 'react-confetti';
import { coinFlipContractConfig, gameTokenContractConfig, faucetContractConfig } from './config';

// Komponen helper
const Coin = ({ isFlipping }) => (
    <div className="coin-container">
        <div className={`coin ${isFlipping ? 'flipping' : ''}`}>
            <div className="side heads">H</div>
            <div className="side tails">T</div>
        </div>
    </div>
);

// Komponen Footer
const Footer = () => (
    <footer className="app-footer">
        <div className="footer-content">
            <div className="made-by">
                Made by <strong>vikitoshi</strong>
            </div>
            <div className="footer-links">
                <a href="https://t.me/AirdropInsiderID" target="_blank" rel="noopener noreferrer">Telegram</a>
                <span>|</span>
                <a href="https://x.com/0xmigoz" target="_blank" rel="noopener noreferrer">Twitter</a>
            </div>
        </div>
    </footer>
);


function App() {
    // State untuk koneksi & kontrak
    const [provider, setProvider] = useState(null);
    const [account, setAccount] = useState(null);
    const [coinFlipContract, setCoinFlipContract] = useState(null);
    const [gameTokenContract, setGameTokenContract] = useState(null);
    const [faucetContract, setFaucetContract] = useState(null);

    // State untuk data game
    const [tokenBalance, setTokenBalance] = useState("0");
    const [houseBalance, setHouseBalance] = useState("0");
    const [betAmount, setBetAmount] = useState("10");
    const [gameHistory, setGameHistory] = useState([]);

    // State untuk UI/UX
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState("Connect your wallet to start.");
    const [txHash, setTxHash] = useState(null);
    const [isFlipping, setIsFlipping] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    // Efek Suara
    const flipSoundRef = useRef(null);
    const winSoundRef = useRef(null);
    const loseSoundRef = useRef(null);
    
    // Ukuran window untuk confetti
    const { width, height } = useWindowSize();

    // Inisialisasi Provider
    useEffect(() => {
        if (window.ethereum) {
            const newProvider = new ethers.BrowserProvider(window.ethereum);
            setProvider(newProvider);
        } else {
            setMessage("Please install MetaMask to play!");
        }
    }, []);

    // Fungsi untuk memperbarui saldo
    const updateBalances = async (gtContract, cfContract, currentAccount) => {
        if (!gtContract || !cfContract || !currentAccount) return;
        const playerBal = await gtContract.balanceOf(currentAccount);
        const houseBal = await gtContract.balanceOf(cfContract.target);
        setTokenBalance(ethers.formatUnits(playerBal, 18));
        setHouseBalance(ethers.formatUnits(houseBal, 18));
    };

    // Fungsi utama untuk koneksi dompet
    const connectWallet = async () => {
        if (!provider) return;
        setIsProcessing(true);
        setMessage("Connecting...");
        try {
            const signer = await provider.getSigner();
            const currentAccount = await signer.getAddress();

            const cfContract = new ethers.Contract(coinFlipContractConfig.address, coinFlipContractConfig.abi, signer);
            const gtContract = new ethers.Contract(gameTokenContractConfig.address, gameTokenContractConfig.abi, signer);
            const fContract = new ethers.Contract(faucetContractConfig.address, faucetContractConfig.abi, signer);
            
            setCoinFlipContract(cfContract);
            setGameTokenContract(gtContract);
            setFaucetContract(fContract);
            setAccount(currentAccount);
            
            await updateBalances(gtContract, cfContract, currentAccount);
            setMessage("Wallet connected. Good luck!");
        } catch (err) {
            console.error(err);
            setMessage("Failed to connect wallet.");
        }
        setIsProcessing(false);
    };

    // Fungsi untuk request token dari Faucet
    const requestFaucetTokens = async () => {
        if (!faucetContract) return;
        setIsProcessing(true);
        setTxHash(null);
        setMessage("Requesting tokens from faucet...");
        try {
            const tx = await faucetContract.requestTokens();
            const receipt = await tx.wait();
            setTxHash(receipt.hash);
            await updateBalances(gameTokenContract, coinFlipContract, account);
            setMessage("100 GMTK received from faucet!");
        } catch (err) {
            console.error(err);
            setMessage(err.reason || "Faucet request failed.");
        }
        setIsProcessing(false);
    };

    const handleFlip = async (choiceIsHeads) => {
        if (!coinFlipContract || !gameTokenContract) return;

        setIsProcessing(true);
        setTxHash(null);
        setMessage("Preparing your bet...");
        
        try {
            const amount = ethers.parseUnits(betAmount, 18);
            setMessage("1/2: Approving token transfer...");
            const approveTx = await gameTokenContract.approve(coinFlipContractConfig.address, amount);
            await approveTx.wait();

            setMessage("2/2: Placing bet... The coin is in the air!");
            flipSoundRef.current.play();
            setIsFlipping(true);

            const placeBetTx = await coinFlipContract.placeBet(amount, choiceIsHeads);
            const receipt = await placeBetTx.wait();
            
            setTxHash(receipt.hash);
            
            const betSettledEvent = receipt.logs.find(e => e.fragment && e.fragment.name === 'BetSettled');
            const { won, payout } = betSettledEvent.args;

            const newHistoryItem = {
                choice: choiceIsHeads ? "Heads" : "Tails",
                amount: betAmount,
                won,
                txHash: receipt.hash
            };
            setGameHistory(prev => [newHistoryItem, ...prev.slice(0, 4)]);

            if (won) {
                winSoundRef.current.play();
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 5000); 
                setMessage(`YOU WON! Payout: ${ethers.formatUnits(payout, 18)} GMTK.`);
            } else {
                loseSoundRef.current.play();
                setMessage("You lost this time. Better luck next time!");
            }
            
            await updateBalances(gameTokenContract, coinFlipContract, account);

        } catch (err) {
            console.error(err);
            setMessage(err.reason || "Transaction failed.");
        } finally {
            setIsFlipping(false);
            setIsProcessing(false);
        }
    };
    
    const explorerUrl = "https://explorer.uomi.ai/tx/";

    return (
        <div className="App">
            {showConfetti && <Confetti width={width} height={height} />}
            <audio ref={flipSoundRef} src="/sounds/coin-flip.mp3" preload="auto"></audio>
            <audio ref={winSoundRef} src="/sounds/win.mp3" preload="auto"></audio>
            <audio ref={loseSoundRef} src="/sounds/lose.mp3" preload="auto"></audio>

            <header className="App-header">
                <h1>CryptoFlip</h1>
                {!account ? (
                    <button className="btn btn-primary" onClick={connectWallet} disabled={isProcessing}>
                        Connect Wallet
                    </button>
                ) : (
                    <div className="account-info">
                        <p>Connected: {`${account.substring(0, 6)}...${account.substring(account.length - 4)}`}</p>
                        <p className="balance">Your Balance: {parseFloat(tokenBalance).toFixed(2)} GMTK</p>
                    </div>
                )}
            </header>

            {account && (
                <>
                    <div className="glass-card">
                         <div className="house-balance">
                            <p>💰 House Pool: <strong>{parseFloat(houseBalance).toFixed(2)} GMTK</strong></p>
                        </div>
                        <div className="status-message">
                            {message}
                            {txHash && (
                                <>
                                    <br />
                                    <a href={`${explorerUrl}${txHash}`} target="_blank" rel="noopener noreferrer">
                                        View Transaction
                                    </a>
                                </>
                            )}
                        </div>
                        <Coin isFlipping={isFlipping} />
                        <div className="bet-controls">
                            <input type="range" min="10" max="50" value={betAmount} onChange={(e) => setBetAmount(e.target.value)} disabled={isProcessing} />
                            <p>Bet Amount: <strong>{betAmount} GMTK</strong></p>
                        </div>
                        <div className="choice-buttons">
                            <button className="btn btn-heads" onClick={() => handleFlip(true)} disabled={isProcessing || isFlipping}>Bet on Heads</button>
                            <button className="btn btn-tails" onClick={() => handleFlip(false)} disabled={isProcessing || isFlipping}>Bet on Tails</button>
                        </div>
                    </div>
                    
                    <div className="glass-card">
                        <h3>Need Tokens?</h3>
                        <p>Get 100 free GMTK to play.</p>
                        <button className="btn btn-faucet" onClick={requestFaucetTokens} disabled={isProcessing}>
                            {isProcessing ? "Processing..." : "Get Tokens"}
                        </button>
                    </div>

                    {gameHistory.length > 0 && (
                        <div className="glass-card history-panel">
                            <h3>Recent Plays</h3>
                            <ul className="history-list">
                                {gameHistory.map((item, index) => (
                                    <li key={index} className={`history-item ${item.won ? 'win' : 'loss'}`}>
                                        <span>Bet {item.amount} on {item.choice}</span>
                                        <span>{item.won ? 'Won' : 'Lost'}</span>
                                        <a href={`${explorerUrl}${item.txHash}`} target="_blank" rel="noopener noreferrer">Transaction Explorer</a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            <Footer />
        </div>
    );
}

export default App;